import React, { ComponentType, useEffect, useRef, useState } from 'react'
import { Route, Switch, useLocation } from 'react-router'
import { PageComponent } from '@core/types'
import { AppLayout } from 'src/layout/AppLayout'
import { isAuthZone, isMyZone } from 'src/routing/pageZoneDetector'
import { AppContext, getAppContext } from 'src/context/AppContext'
import { pages as routes } from 'src/routing/pages'

import NotFoundPage from 'src/not-found/NotFoundPage'
import LimitedViewWrapper from './LimitedViewWrapper'
import {
    getGeolocation,
    isAndroidNativeApp,
    isIosNativeApp,
    isMobileWeb,
    useGeolocation
} from './geo/locationService'
import { useSearchParams } from './app/useSearchParams'
import InfoMessages from './business/InfoMessages/InfoMessages'
import { useTran } from './i18n/useTran'
import { msg } from './i18n/texts/keys'
import { RentStatus } from './api/defs/enums.type'
import { useInfoQueue } from './ui/alert/useInfoQueue'
import InfoToaster from './ui/alert/InfoToaster'
import InfoBackdrop from './ui/alert/InfoBackdrop'
import { INFO_TOAST_STATE_CHANGE } from './ui/alert/infoOverlayBus'
import { overdueStore } from './context/InfoMessageContext'
import { useSwipeNavigation } from './hooks/useSwipeNavigation'

const MY_RENTS_PREFIX = (routes as any)?.MyRents?.path ?? '/my/rents'

const shouldRedirectToHome = (hasSession: boolean, path: string) => {
    if (hasSession && isAuthZone(path)) return true
    return false
}

const shouldRedirectToLogin = (hasSession: boolean, path: string) => {
    if (!hasSession && isMyZone(path)) return true
    return false
}

const guardRoutes = (ctx: AppContext) => {
    const session = ctx.user.sessionStore.value()
    const r = ctx.router

    const redirectIfNeeded = (session: boolean) => {
        if (shouldRedirectToHome(session, ctx.router.getLocation().path)) {
            r.replace('/')
        }
        if (shouldRedirectToLogin(session, ctx.router.getLocation().path))
            r.replace(routes.Login.to())
    }

    if (session !== null) {
        redirectIfNeeded(session)
    }

    ctx.user.sessionStore.changes().subscribe(s => {
        if (s !== null) redirectIfNeeded(s)
    })
}

const changeTitle = (pathname: string) => {
    if (
        pathname !== routes.Landing.path &&
        pathname !== routes.WarehouseSearch.path &&
        !pathname.includes('/location')
    ) {
        document.title = 'BOXYLAND | All you need is space'
    }
}

const redirectToMobileLanding = (ctx: AppContext) => {
    ctx.router.replace(routes.LandingWebView.path + '?isRedirected=true')
}

const redirectToWebLanding = (ctx: AppContext) => {
    ctx.router.replace(routes.Landing.path + '?isRedirected=true')
}

export const App: ComponentType<{ pages: PageComponent[] }> = ({
    pages: pageList
}) => {
    const ctx = getAppContext()
    const session = ctx.user.sessionStore.value()
    const t = useTran()
    const location = useLocation()
    const isRedirected = useSearchParams().get('isRedirected') === 'true'
    const geolocation = useGeolocation()
    const isRedirecting = useRef(false)
    const isPaid = (s: unknown) => String(s ?? '').toUpperCase() === 'PAID'
    const isUnpaidRent = (r: any) => !isPaid(r?.status)
    const queue = useInfoQueue()
    const [backdropCount, setBackdropCount] = useState(0)

    const isNativeApp =
        (isIosNativeApp() || isAndroidNativeApp()) && location.pathname === '/'

    const [isOnMyRents, setIsOnMyRents] = useState(
        ctx.router.getLocation().path.startsWith(MY_RENTS_PREFIX)
    )

    useSwipeNavigation({
        enabled: isIosNativeApp() || isAndroidNativeApp() || isMobileWeb(),
        noSwipeSelector:
            ".no-swipe, .swiper, .slick-slider, .image-gallery, [data-no-swipe='true']"
    })

    useEffect(() => {
        const onState = (e: Event) => {
            const { count } = (e as CustomEvent<{ count: number }>).detail
            setBackdropCount(count)
        }
        window.addEventListener(INFO_TOAST_STATE_CHANGE, onState)
        return () =>
            window.removeEventListener(INFO_TOAST_STATE_CHANGE, onState)
    }, [])

    useEffect(() => {
        const sub = ctx.router.locationChanges().subscribe(loc => {
            setIsOnMyRents(loc.path.startsWith(MY_RENTS_PREFIX))
        })
        return () => sub.unsubscribe()
    }, [])

    useEffect(() => {
        const TOAST_ID = 'overdue-payment-toast'
        const sub = ctx.user.sessionStore.changes().subscribe(async session => {
            if (session) {
                await checkOverdue()
            } else {
                queue.closeById(TOAST_ID)
            }
        })
        return () => sub.unsubscribe()
    }, [])

    const [hasUnpaid, setHasUnpaid] = useState<boolean | null>(null)

    const checkOverdue = async (): Promise<boolean> => {
        if (!session) {
            setHasUnpaid(false)
            return false
        }
        try {
            if (!ctx.myZoneApi?.rents) {
                setHasUnpaid(false)
                overdueStore.change(false)
                return false
            }

            try {
                const filtered: any = await (ctx.myZoneApi.rents as any)({
                    unpaid: true
                })
                if (Array.isArray(filtered)) {
                    const has =
                        filtered.length > 0 || filtered.some(isUnpaidRent)
                    setHasUnpaid(has)
                    overdueStore.change(has)
                    return has
                }
            } catch {}

            const all: any = await (ctx.myZoneApi.rents as any)()
            const has =
                Array.isArray(all) &&
                all.some((r: any) => r.status !== RentStatus.PAID)
            setHasUnpaid(has)
            overdueStore.change(has)
            return has
        } catch (e) {
            console.error('[overdue] check error', e)
            setHasUnpaid(false)
            overdueStore.change(false)
            return false
        }
    }

    // useEffect(() => {
    //     queue.enqueue({
    //         id: 'marketing-branch-open',
    //         priority: 10,
    //         render: ({ close }) => <MarketingBranchOpen open={true} onClose={close} />
    //     });

    //     queue.enqueue({
    //         id: 'app-update-available',
    //         priority: 20,
    //         render: ({ close }) => <AppUpdateAvailable open={true} onClose={close} />
    //     });

    //     queue.enqueue({
    //         id: 'feature-spotlight',
    //         priority: 15,
    //         render: ({ close }) => <FeatureSpotlight open={true} onClose={close} />
    //     });
    // }, []);

    useEffect(() => {
        void checkOverdue()
        const onFocus = () => void checkOverdue()
        const onVisibility = () => {
            if (document.visibilityState === 'visible') void checkOverdue()
        }
        const sub = ctx.router
            .locationChanges()
            .subscribe(() => void checkOverdue())

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibility)
            sub.unsubscribe()
        }
    }, [])

    useEffect(() => {
        const TOAST_ID = 'overdue-payment-toast'
        const suppressedOnce =
            sessionStorage.getItem('suppressOverdueModalOnce') === 'true'

        if (hasUnpaid && !isOnMyRents && !suppressedOnce) {
            queue.enqueue({
                id: TOAST_ID,
                priority: 30,
                render: ({ close }) => (
                    <InfoToaster
                        open={true}
                        onClose={close}
                        tone="warning"
                        kind="transaction"
                        title={t(msg.myZone.myRents.paymentOverdueTitle)}
                        text={t(msg.myZone.myRents.paymentOverdueText)}
                        primaryCta={{
                            label: t(msg.myZone.myRents.pay),
                            onClick: () => {
                                sessionStorage.setItem(
                                    'suppressOverdueModalOnce',
                                    'true'
                                )
                                close()
                                ctx.router.replace(
                                    (routes as any)?.MyRents?.path ??
                                        '/my/rents'
                                )
                            }
                        }}
                    />
                )
            })
        } else {
            queue.closeById(TOAST_ID)
        }
    }, [hasUnpaid, isOnMyRents])

    useEffect(() => {
        try {
            const nav =
                (performance.getEntriesByType?.(
                    'navigation'
                )?.[0] as PerformanceNavigationTiming) || undefined
            const isReload =
                (nav && nav.type === 'reload') ||
                (performance as any)?.navigation?.type === 1

            if (isReload) {
                sessionStorage.removeItem('suppressOverdueModalOnce')
            }
        } catch {}
    }, [])

    useEffect(() => {
        if (isNativeApp && isRedirected) {
            sessionStorage.setItem('setRedirect', 'true')
            isRedirecting.current = true
        }
    }, [isRedirected, location.pathname])

    useEffect(() => {
        guardRoutes(ctx)
    }, [])

    useEffect(() => {
        changeTitle(location.pathname)
    }, [location])

    useEffect(() => {
        if (isNativeApp) {
            const hasRedirect = sessionStorage.getItem('setRedirect')

            if (hasRedirect === 'true' || isRedirected) {
                isRedirecting.current = false
                redirectToWebLanding(ctx)
            } else {
                isRedirecting.current = true
                sessionStorage.removeItem('setRedirect')
                redirectToMobileLanding(ctx)
            }
        }
    }, [isNativeApp, isRedirected])

    useEffect(() => {
        if (isRedirecting.current) return

        // disable geolocation on QrAccess page
        if (location.pathname === routes.QrAccess.path) return

        if (
            geolocation.permission === 'denied' ||
            geolocation.permission === 'unsupported'
        ) {
            return
        }

        const refreshGeolocation = async () => {
            try {
                await getGeolocation(true)
            } catch (error) {
                console.warn('Background geolocation refresh failed:', error)
            }
        }

        // Run immediately
        refreshGeolocation()

        const intervalId = setInterval(() => refreshGeolocation(), 3 * 60 * 1000) // 3 minutes

        return () => clearInterval(intervalId)
    }, [geolocation, location.pathname])

    return (
        <>
            <AppLayout>
                <Switch>
                    <LimitedViewWrapper>
                        {pageList.map(p => (
                            <Route
                                key={p.path}
                                exact={true}
                                path={p.path}
                                component={p.comp}
                            />
                        ))}
                    </LimitedViewWrapper>
                    <Route path="*" exact={true} component={NotFoundPage} />
                </Switch>
                <InfoMessages />
            </AppLayout>
            <InfoBackdrop active={backdropCount > 0} />
            {(() => {
                const current = queue.current
                return current
                    ? current.render({
                          close: () => queue.closeById(current.id)
                      })
                    : null
            })()}
        </>
    )
}
